// Resolve a player's effective building / vigor-zone rules: sparse per-player
// overrides fall back to the match's pinned GameConfig. A key present on the
// player is a full entry (same shape as the match table); a missing key means
// "use the match default".
function getOverride(source, key) {
    if (!source)
        return undefined;
    if (typeof source.get === "function") {
        return source.get(key);
    }
    return source[key];
}
function asResourceAmount(cost) {
    const arr = Array.from(cost);
    if (arr.length === 4) {
        return [arr[0], arr[1], arr[2], arr[3]];
    }
    // Wildcard costs (e.g. `[1]` = "1 of any resource") are length ≠ 4 at runtime.
    return arr;
}
function asQuad(bonus) {
    const arr = Array.from(bonus);
    return [arr[0] ?? 0, arr[1] ?? 0, arr[2] ?? 0, arr[3] ?? 0];
}
/** Convert a synced / plain override entry into a plain `BuildingConfig`. */
export function buildingConfigFromOverride(o) {
    const cfg = {
        cost: asResourceAmount(o.cost),
        range: {
            build: o.range.build,
            ...(o.range.attack ? { attack: o.range.attack } : {}),
            ...(o.range.defend ? { defend: o.range.defend } : {}),
        },
    };
    if (o.vigorPenalty)
        cfg.vigorPenalty = true;
    if (o.permanentResourceCost) {
        cfg.permanentResourceCost = o.permanentResourceCost;
    }
    if (o.blockOpponentBuild)
        cfg.blockOpponentBuild = true;
    if (o.stolenResourceCount)
        cfg.stolenResourceCount = o.stolenResourceCount;
    return cfg;
}
/** Convert a synced / plain override entry into a plain `VigorZoneData`. */
export function vigorZoneFromOverride(o) {
    return {
        name: o.name,
        permanentResourceBonus: asQuad(o.permanentResourceBonus),
        oneTimeResourceBonus: asQuad(o.oneTimeResourceBonus),
        vigorPenalty: o.vigorPenalty,
        barracksAttackUpkeep: o.barracksAttackUpkeep,
    };
}
/**
 * Effective building config for `type`: player's sparse override if present,
 * otherwise the match snapshot.
 */
export function resolveBuilding(match, player, type) {
    const override = getOverride(player.buildings, type);
    if (override)
        return buildingConfigFromOverride(override);
    return match.buildings[type];
}
/**
 * Effective vigor-zone data for altitude `level` ("0".."4"): player's sparse
 * override if present, otherwise the match snapshot.
 */
export function resolveVigorZone(match, player, level) {
    const override = getOverride(player.vigorZones, String(level));
    if (override)
        return vigorZoneFromOverride(override);
    return match.vigorZones[level];
}
/** Deep-clone a plain building config (for seeding a player override entry). */
export function cloneBuildingConfig(cfg) {
    return {
        cost: [...cfg.cost],
        range: { ...cfg.range },
        ...(cfg.vigorPenalty ? { vigorPenalty: true } : {}),
        ...(cfg.permanentResourceCost
            ? { permanentResourceCost: cfg.permanentResourceCost }
            : {}),
        ...(cfg.blockOpponentBuild ? { blockOpponentBuild: true } : {}),
        ...(cfg.stolenResourceCount
            ? { stolenResourceCount: cfg.stolenResourceCount }
            : {}),
    };
}
/** Deep-clone a plain vigor-zone entry (for seeding a player override entry). */
export function cloneVigorZone(z) {
    return {
        name: z.name,
        permanentResourceBonus: [...z.permanentResourceBonus],
        oneTimeResourceBonus: [...z.oneTimeResourceBonus],
        vigorPenalty: z.vigorPenalty,
        barracksAttackUpkeep: z.barracksAttackUpkeep,
    };
}
//# sourceMappingURL=player_config.js.map